import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock } from "@phosphor-icons/react";

/**
 * Idle auto-logout — role-aware timeout.
 *
 * - Client users (`role` starts with `client_`) sign out after 7 min.
 *   Tighter window because the client portal touches billing,
 *   proposals, and contract docs that need shorter exposure on a
 *   shared device.
 * - Internal Eyvo team members (every non-`client_*` role) sign out
 *   after 30 min. Internal staff routinely flip away to ticket
 *   research, customer calls, and other browser tabs; 30 min keeps
 *   them logged in across the typical task-switch.
 *
 * - Resets timer on `mousedown`, `mousemove` (throttled), `keydown`,
 *   `scroll`, `touchstart`, `wheel`.
 * - When idle for the role-specific limit, calls `logout()` and
 *   routes to /.
 * - Subtle pill renders bottom-right whenever the user is signed in;
 *   pill turns amber in last 60s and red in last 15s.
 */
const CLIENT_IDLE_LIMIT_MS   = 7  * 60 * 1000;   //  7 min — client_* roles
const INTERNAL_IDLE_LIMIT_MS = 30 * 60 * 1000;   // 30 min — Eyvo staff
const TICK_MS = 1000;                       // 1 s
const MOUSEMOVE_THROTTLE_MS = 1000;         // ignore micro-mouse-jitter
const WARN_AT_MS = 60 * 1000;               // last 60s = amber
const URGENT_AT_MS = 15 * 1000;             // last 15s = red

// Roles whose primary identity is "external client". Everything else
// (platform_admin, delivery_admin, project_manager, developer,
// view_only, …) is treated as internal Eyvo staff.
function isClientRole(role) {
  return typeof role === "string" && role.startsWith("client_");
}
function idleLimitForUser(user) {
  if (!user) return INTERNAL_IDLE_LIMIT_MS;
  if (isClientRole(user.role)) return CLIENT_IDLE_LIMIT_MS;
  if (Array.isArray(user.roles) && user.roles.length > 0
      && user.roles.every(isClientRole)) {
    return CLIENT_IDLE_LIMIT_MS;
  }
  return INTERNAL_IDLE_LIMIT_MS;
}
function idleLimitLabel(limitMs) {
  return `${Math.round(limitMs / 60000)} minutes`;
}

function formatMMSS(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function IdleTimer() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const idleLimitMs = idleLimitForUser(user);
  const [remaining, setRemaining] = useState(idleLimitMs);
  const lastActivityRef = useRef(Date.now());
  const lastMouseMoveRef = useRef(0);
  const intervalRef = useRef(null);
  const firedRef = useRef(false);

  const handleActivity = useCallback((evt) => {
    if (evt && evt.type === "mousemove") {
      const now = Date.now();
      if (now - lastMouseMoveRef.current < MOUSEMOVE_THROTTLE_MS) return;
      lastMouseMoveRef.current = now;
    }
    lastActivityRef.current = Date.now();
    firedRef.current = false;
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    // Reset the visible remaining-time whenever the active user's role
    // changes (e.g. impersonation flip), so the pill doesn't display a
    // stale countdown from the previous role's limit.
    lastActivityRef.current = Date.now();
    firedRef.current = false;
    setRemaining(idleLimitMs);

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));

    intervalRef.current = setInterval(async () => {
      const elapsed = Date.now() - lastActivityRef.current;
      const left = idleLimitMs - elapsed;
      setRemaining(left);
      if (left <= 0 && !firedRef.current) {
        firedRef.current = true;
        try {
          await logout();
        } catch {
          /* ignore */
        }
        toast.warning(`You were signed out after ${idleLimitLabel(idleLimitMs)} of inactivity.`, {
          duration: 6000,
          id: "idle-logout",
        });
        navigate("/", { replace: true });
      }
    }, TICK_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, idleLimitMs, handleActivity, logout, navigate]);

  if (!user) return null;

  const remainingMs = Math.max(0, remaining);
  let bg = "bg-white";
  let border = "border-[#E5E5E5]";
  let text = "text-[#525252]";
  let iconColor = "text-[#525252]";
  if (remainingMs <= URGENT_AT_MS) {
    bg = "bg-[#FEF2F2]";
    border = "border-[#DC2626]";
    text = "text-[#DC2626]";
    iconColor = "text-[#DC2626]";
  } else if (remainingMs <= WARN_AT_MS) {
    bg = "bg-[#FFFBEB]";
    border = "border-[#F59E0B]";
    text = "text-[#B45309]";
    iconColor = "text-[#B45309]";
  }

  return (
    <div
      className={`fixed bottom-3 right-3 z-50 select-none flex items-center gap-1.5 px-2.5 py-1 ${bg} ${border} border rounded-full shadow-sm text-[11px] font-mono ${text}`}
      data-testid="idle-timer-pill"
      title={`You will be signed out automatically after ${idleLimitLabel(idleLimitMs)} of inactivity.`}
    >
      <Clock size={12} weight="bold" className={iconColor} />
      <span data-testid="idle-timer-remaining">Idle {formatMMSS(remainingMs)}</span>
    </div>
  );
}
